import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail, Languages, Briefcase, Clock } from "lucide-react";
import { format } from "date-fns";

export default function AdminApplications() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [working, setWorking] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/therapist-applications");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load applications.");
      setItems([]);
    }
  };

  useEffect(() => {
    if (!loading && user?.role === "admin") load();
  }, [loading, user]);

  if (loading || user === null) return <div className="min-h-[50vh] flex items-center justify-center text-mindora-muted">loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="admin-no-access">
        <h1 className="font-serif text-4xl text-mindora-ink leading-tight">Admin only.</h1>
        <p className="text-mindora-muted mt-3">This page is for the Mindora admin team. If that&apos;s you, sign in with the admin account.</p>
      </div>
    );
  }

  const act = async (id, kind) => {
    setWorking(`${id}-${kind}`);
    try {
      const { data } = await api.post(`/admin/applications/${id}/${kind}`);
      if (kind === "approve") {
        toast.success(`Approved · login: ${data.login_email}`);
      } else {
        toast.success("Application rejected — polite email sent.");
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update.");
    } finally {
      setWorking(null);
    }
  };

  const pending = (items || []).filter((a) => a.status === "received");
  const reviewed = (items || []).filter((a) => a.status !== "received");

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="page-admin-applications">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
        <span className="editorial-rule" />Admin · counselor applications
      </div>
      <h1 className="font-serif text-5xl lg:text-6xl text-mindora-ink leading-tight">
        {pending.length === 0
          ? <>All caught up.</>
          : <><em className="italic text-mindora-red">{pending.length}</em> {pending.length === 1 ? "application" : "applications"} waiting for you.</>}
      </h1>
      <p className="mt-4 text-mindora-muted max-w-2xl">
        Approving creates the counselor&apos;s public profile + login account and emails them their credentials. Rejecting sends a polite note.
      </p>

      {items === null && <div className="mt-10 text-mindora-muted">loading…</div>}

      {pending.length > 0 && (
        <section className="mt-14">
          <h2 className="font-serif text-3xl text-mindora-ink">Pending</h2>
          <div className="mt-6 space-y-6" data-testid="admin-pending-list">
            {pending.map((a) => (
              <article key={a.id} className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay" data-testid={`admin-application-${a.id}`}>
                <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-3xl text-mindora-ink leading-tight">{a.name}</h3>
                    <div className="text-xs uppercase tracking-[0.18em] text-mindora-red mt-1">{a.title}</div>
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-mindora-muted">
                      <span className="flex items-center gap-1.5"><Mail size={12} /> {a.email}</span>
                      <span className="flex items-center gap-1.5"><Briefcase size={12} /> {a.years_experience} yrs</span>
                      <span className="flex items-center gap-1.5"><Languages size={12} /> {a.languages.join(", ")}</span>
                      <span className="flex items-center gap-1.5"><Clock size={12} /> {a.created_at ? format(new Date(a.created_at), "PPP") : "—"}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-serif text-2xl text-mindora-ink">₹{a.session_price?.toLocaleString("en-IN")}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-mindora-muted">per session</span>
                  </div>
                </header>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {a.specialties.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-[0.16em] text-mindora-ink/70 border border-mindora-line rounded-full px-2.5 py-0.5">{s}</span>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-1.5">Bio</div>
                    <p className="text-sm text-mindora-ink/90 whitespace-pre-wrap leading-relaxed">{a.bio}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-1.5">Why Mindora</div>
                    <p className="text-sm text-mindora-ink/90 whitespace-pre-wrap leading-relaxed">{a.why_mindora}</p>
                  </div>
                </div>

                {a.linkedin_url && (
                  <div className="mt-4 text-sm">
                    <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="text-mindora-red underline hover:no-underline">LinkedIn ↗</a>
                  </div>
                )}

                <div className="mt-7 flex flex-wrap gap-3 pt-5 border-t border-mindora-line">
                  <Button
                    onClick={() => act(a.id, "approve")}
                    disabled={working === `${a.id}-approve`}
                    data-testid={`admin-approve-${a.id}`}
                    className="btn-primary rounded-full px-5 py-2 h-auto text-sm inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={14} /> Approve & invite
                  </Button>
                  <Button
                    onClick={() => act(a.id, "reject")}
                    disabled={working === `${a.id}-reject`}
                    data-testid={`admin-reject-${a.id}`}
                    variant="outline"
                    className="rounded-full px-5 py-2 h-auto text-sm border-mindora-line text-mindora-ink hover:bg-mindora-line inline-flex items-center gap-1.5"
                  >
                    <XCircle size={14} /> Politely decline
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {reviewed.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif text-2xl text-mindora-ink">Already reviewed</h2>
          <div className="mt-5 space-y-2.5" data-testid="admin-reviewed-list">
            {reviewed.map((a) => (
              <div key={a.id} className="rounded-2xl border border-mindora-line bg-mindora-bg px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-serif text-lg text-mindora-ink leading-tight">{a.name}</div>
                  <div className="text-xs text-mindora-muted">{a.email} · {a.title}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full ${
                  a.status === "approved"
                    ? "bg-mindora-red/10 text-mindora-red"
                    : "border border-mindora-line text-mindora-muted"
                }`}>{a.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
